<template>
  <Teleport to="body">
    <div class="enterprise-access-mask" role="presentation">
      <section
        class="enterprise-access-dialog"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="descriptionId"
      >
        <span class="enterprise-access-dialog__icon">
          <WorkbenchIcon name="lock" :size="24" />
        </span>
        <h1 :id="titleId">{{ t('enterprise_workspace_block_title') }}</h1>
        <p :id="descriptionId">
          {{ t(isGuest ? 'enterprise_workspace_block_guest_description' : 'enterprise_workspace_block_registered_description') }}
        </p>
        <div class="enterprise-access-dialog__actions">
          <button type="button" class="is-secondary" @click="$emit('secondary')">
            {{ t(isGuest ? 'enterprise_workspace_login_signup' : 'enterprise_workspace_back_home') }}
          </button>
          <button type="button" class="is-primary" @click="$emit('plans')">
            {{ t('enterprise_workspace_view_plans') }}
          </button>
        </div>
        <button type="button" class="enterprise-access-dialog__link" @click="$emit('link')">
          {{ t(isGuest ? 'enterprise_workspace_back_home' : 'enterprise_workspace_switch_account') }}
        </button>
      </section>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import WorkbenchIcon from '@/components/Workbench/WorkbenchIcon.vue'

const props = defineProps<{ mode: 'guest' | 'registered' }>()
defineEmits<{
  (event: 'secondary'): void
  (event: 'plans'): void
  (event: 'link'): void
}>()

const { t } = useI18n()
const titleId = useId()
const descriptionId = useId()
const isGuest = computed(() => props.mode === 'guest')
</script>

<style scoped lang="scss">
.enterprise-access-mask {
  position: fixed;
  z-index: 4000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 60px;
  background: rgba(31, 35, 41, 0.45);
}

.enterprise-access-dialog {
  box-sizing: border-box;
  display: flex;
  width: min(440px, calc(100vw - 40px));
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 30px 28px 26px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 16px 44px rgba(10, 35, 77, 0.2);
  color: #1f2329;
  text-align: center;

  h1,
  p {
    margin: 0;
  }

  h1 {
    font-size: 17px;
    font-weight: 700;
    line-height: 1.4;
  }

  p {
    color: #646a73;
    font-size: 12.5px;
    line-height: 1.6;
  }
}

.enterprise-access-dialog__icon {
  display: flex;
  width: 52px;
  height: 52px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eaf3ff;
  color: #2f80ed;
}

.enterprise-access-dialog__actions {
  display: flex;
  width: 100%;
  gap: 12px;
  padding-top: 6px;

  button {
    box-sizing: border-box;
    min-width: 0;
    height: 42px;
    flex: 1 1 0;
    padding: 0 12px;
    border-radius: 10px;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
  }

  .is-secondary {
    border: 1px solid #d9dee5;
    background: #fff;
    color: #51565d;
  }

  .is-primary {
    border: 1px solid #2f80ed;
    background: #2f80ed;
    color: #fff;
  }
}

.enterprise-access-dialog__link {
  border: 0;
  background: transparent;
  color: #2f80ed;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
}

@media (max-width: 520px) {
  .enterprise-access-mask {
    padding: 24px 20px;
  }

  .enterprise-access-dialog__actions {
    flex-direction: column;
  }
}
</style>
